import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Space, Popconfirm, message, Typography, Card, Tag, Image } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, FileImageOutlined, StarOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { Logo } from '../../types/logo';
import { logoService } from '../../services/logoService';
import LogoForm from './LogoForm';

const { Title } = Typography;

const LogosPage: React.FC = () => {
  const { t } = useTranslation();
  const [logos, setLogos] = useState<Logo[]>([]);
  const [loading, setLoading] = useState(false);
  const [formVisible, setFormVisible] = useState(false);
  const [selectedLogo, setSelectedLogo] = useState<Logo | undefined>(undefined);

  // Function to fetch all logos
  const fetchLogos = useCallback(async () => {
    setLoading(true);
    try {
      const data = await logoService.getAllLogos();
      setLogos(data);
    } catch (error) {
      console.error('Error fetching logos:', error);
      message.error(t('logos.notifications.error', { message: (error as Error).message }));
    } finally {
      setLoading(false);
    }
  }, [t]);

  // Load logos on component mount
  useEffect(() => {
    fetchLogos();
  }, [fetchLogos]);

  // Handle editing a logo
  const handleEdit = (logo: Logo) => {
    setSelectedLogo(logo);
    setFormVisible(true);
  };

  // Handle creating a new logo
  const handleCreate = () => {
    setSelectedLogo(undefined);
    setFormVisible(true);
  };

  // Handle deleting a logo
  const handleDelete = async (id: number) => {
    try {
      await logoService.deleteLogo(id);
      message.success(t('logos.notifications.delete_success'));
      fetchLogos(); // Refresh the list
    } catch (error) {
      console.error('Error deleting logo:', error);
      message.error(t('logos.notifications.delete_error', { message: (error as Error).message }));
    }
  };

  // Handle form submission success
  const handleFormSuccess = () => {
    fetchLogos(); // Refresh the list
  };

  // Handle form close
  const handleFormClose = () => {
    setFormVisible(false);
    setSelectedLogo(undefined);
  };

  // Get logo file URL for display
  const getLogoUrl = (logoFile: string) => {
    if (logoFile.startsWith('http')) {
      return logoFile;
    }
    // Extract filename from path (e.g., "storage/logos/filename.png" -> "filename.png")
    const filename = logoFile.split('/').pop();
    const baseUrl = process.env.NODE_ENV === 'development' ? 'http://localhost:8080' : '';
    return `${baseUrl}/api/logofiles/${filename}`;
  };

  const columns = [
    {
      title: t('logos.table.preview'),
      dataIndex: 'logo_file',
      key: 'preview',
      width: 100,
      render: (logoFile: string) => (
        <Image
          width={50}
          height={50}
          src={getLogoUrl(logoFile)}
          alt="Logo preview"
          style={{ objectFit: 'contain' }}
          fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RnG4W+FgYxN"
        />
      ),
    },
    {
      title: t('logos.table.logo_name'),
      dataIndex: 'logo_name',
      key: 'logo_name',
      sorter: (a: Logo, b: Logo) => a.logo_name.localeCompare(b.logo_name),
    },
    {
      title: t('logos.table.logo_file'),
      dataIndex: 'logo_file',
      key: 'logo_file',
      render: (file: string) => {
        if (!file) return '-';
        // Extract filename from path (e.g., "storage/logos/filename.png" -> "filename.png")
        const filename = file.split('/').pop() || '';
        // Use direct backend URL in development to avoid proxy issues
        const baseUrl = process.env.NODE_ENV === 'development' ? 'http://localhost:8080' : '';
        const fileUrl = `${baseUrl}/api/logofiles/${filename}`;
        
        return (
          <Space>
            <a
              href={fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ 
                fontFamily: 'monospace', 
                fontSize: '12px',
                color: '#1890ff',
                textDecoration: 'none'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.textDecoration = 'underline';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.textDecoration = 'none';
              }}
            >
              {file.length > 30 ? `...${file.slice(-30)}` : file}
            </a>
          </Space>
        );
      }
    },
    {
      title: t('logos.table.is_default'),
      dataIndex: 'is_default',
      key: 'is_default',
      width: 120,
      render: (isDefault: boolean) => (
        isDefault ? (
          <Tag icon={<StarOutlined />} color="gold">
            {t('logos.table.default')}
          </Tag>
        ) : (
          <Tag color="default">
            {t('logos.table.not_default')}
          </Tag>
        )
      ),
      sorter: (a: Logo, b: Logo) => (a.is_default ? 1 : 0) - (b.is_default ? 1 : 0),
    },
    {
      title: t('logos.table.created_at'),
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (date: string) => new Date(date).toLocaleString(),
      sorter: (a: Logo, b: Logo) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
    },
    {
      title: t('common.actions'),
      key: 'actions',
      width: 150,
      render: (_: any, record: Logo) => (
        <Space size="middle">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          >
            {t('common.edit')}
          </Button>
          <Popconfirm
            title={t('logos.delete_confirm_title')}
            description={t('logos.delete_confirm_description')}
            onConfirm={() => handleDelete(record.id)}
            okText={t('common.yes')}
            cancelText={t('common.no')}
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              size="small"
            >
              {t('common.delete')}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title level={2} style={{ margin: 0 }}>
            <FileImageOutlined style={{ marginRight: '8px' }} />
            {t('logos.page_title')}
          </Title>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreate}
          >
            {t('logos.create_button')}
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={logos}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              t('common.table_pagination', {
                start: range[0],
                end: range[1],
                total,
              }),
          }}
        />
      </Card>

      <LogoForm
        logo={selectedLogo}
        isVisible={formVisible}
        onClose={handleFormClose}
        onSuccess={handleFormSuccess}
      />
    </div>
  );
};

export default LogosPage;
